import { inject, Injectable } from '@angular/core';
import { JobUpdateNotification } from '@core/models/job-updates-notification.model';
import { FirebaseService } from '@core/services/firebase/firebase.service';
import { NotificationService } from '@core/services/notification/notification.service';
import { Action, Selector, State, StateContext, StateToken, Store } from '@ngxs/store';
import { Timestamp } from 'firebase/firestore';
import { catchError, of, Subject, Subscription, takeUntil, tap } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { JobUpdateStateModel } from './job-updates-state.model';
import {
  ClearJobUpdates,
  HandleJobUpdateError,
  ListenForJobUpdates,
  SetCurrentTransactionId,
  StopListeningForJobUpdates,
  UpdateJobUpdates,
} from './job-updates.actions';

const defaults: JobUpdateStateModel = {
  currentTransactionId: null,
  jobRefId: null,
  jobUpdates: [],
};

export const JOB_UPDATES_STATE_TOKEN = new StateToken<JobUpdateStateModel>('jobUpdates');

@State<JobUpdateStateModel>({
  name: JOB_UPDATES_STATE_TOKEN,
  defaults,
})
@Injectable()
export class JobUpdatesState {
  private jobUpdatesUnSubscription = new Subject<void>();
  private activeSubscription: Subscription;

  notificationService: NotificationService = inject(NotificationService);

  constructor(
    private store: Store,
    private readonly firebaseService: FirebaseService
  ) {}

  @Selector()
  static getJobUpdates(state: JobUpdateStateModel) {
    return state.jobUpdates;
  }

  @Selector()
  static getYouAreHereUpdates(state: JobUpdateStateModel) {
    return state.jobUpdates
      .filter(update => update.data['entityType'] === 'YouAreHere')
      .sort((a, b) => {
        return a.timestamp - b.timestamp;
      })[0];
  }

  /**
   * You-Are-Here v2 realtime feed: all `ProgressTree` pushes, latest-first.
   *
   * Deliberately NOT reusing `getYouAreHereUpdates` above — that one sorts
   * ascending and takes `[0]`, i.e. the OLDEST update (a v1 bug). This sorts
   * descending so consumers `.find(...)` the newest match for their
   * (jobRefId, role). Each push is a full ProgressTree envelope; the tree lives
   * at `data.data`, its role at `data.role` (see BE realtime contract §3).
   */
  @Selector()
  static getProgressTreeUpdates(state: JobUpdateStateModel): JobUpdateNotification[] {
    return state.jobUpdates
      .filter(update => update.data?.['entityType'] === 'ProgressTree')
      .sort((a, b) => b.timestamp - a.timestamp);
  }

  @Selector()
  static getLatestJobUpdates(state: JobUpdateStateModel) {
    return state.jobUpdates.length > 0 ? state.jobUpdates[state.jobUpdates.length - 1] : null;
  }

  @Selector()
  static getCurrentTransactionId(state: JobUpdateStateModel) {
    return state.currentTransactionId;
  }

  @Action(SetCurrentTransactionId)
  setCurrentTransactionId(ctx: StateContext<JobUpdateStateModel>, { transactionId }: SetCurrentTransactionId) {
    const { currentTransactionId } = ctx.getState();
    if (!currentTransactionId) {
      ctx.patchState({ currentTransactionId: transactionId ?? uuidv4() });
    }
  }

  @Action(UpdateJobUpdates)
  updateJobUpdates(ctx: StateContext<JobUpdateStateModel>, { jobUpdates }: UpdateJobUpdates) {
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    const existingJobUpdatesSize = ctx.getState().jobUpdates.length;
    const newJobUpdatesSize = jobUpdates.length;

    if (newJobUpdatesSize >= 4) {
      ctx.patchState({ jobUpdates });
    } else {
      ctx.patchState({
        jobUpdates: [...ctx.getState().jobUpdates.slice(newJobUpdatesSize - existingJobUpdatesSize), ...jobUpdates],
      });
    }
  }

  @Action(ClearJobUpdates)
  clearJobUpdates(ctx: StateContext<JobUpdateStateModel>) {
    ctx.patchState(defaults);
  }

  @Action(ListenForJobUpdates)
  listenForJobUpdates(ctx: StateContext<JobUpdateStateModel>, { orgId, jobRefId, transactionId }: ListenForJobUpdates) {
    const { jobRefId: currentJobRefId, currentTransactionId } = ctx.getState();

    const {
      user: { orgId: currentOrgId },
    } = this.store.selectSnapshot(s => s.auth);

    // Clean up existing subscription if changing job
    if (currentJobRefId !== jobRefId) {
      this.cleanupExistingSubscription();
      ctx.patchState({ jobRefId });
    } else {
      return;
    }

    this.jobUpdatesUnSubscription = new Subject<void>();

    this.activeSubscription = this.firebaseService
      .getJobUpdates(orgId ?? currentOrgId, jobRefId, transactionId ?? currentTransactionId)
      .pipe(
        takeUntil(this.jobUpdatesUnSubscription),
        tap(updates => {
          const jobUpdates: JobUpdateNotification[] = updates.map(update => {
            const data = update.payload.doc.data();
            const obj = {
              data,
              entityType: data['entityType'],
              operation: data['operation'],
              orgId: data['orgId'],
              jobRefId: data['jobRefId'],
              transactionId: data['transactionId'],
              // A locally-echoed write carries a null/pending serverTimestamp until
              // the server confirms; guard so one such doc can't throw and take the
              // whole batch (and the subscription) down. The confirming emission
              // carries the real Timestamp.
              timestamp: data['timestamp'] instanceof Timestamp ? (data['timestamp'] as Timestamp).toMillis() : 0,
              error: data['error'] ?? false,
              message: data['message'] ?? '',
            };
            return obj;
          });
          if (jobUpdates.length > 0) {
            const updates = jobUpdates.filter(ju => !ju.error);
            const errors = jobUpdates.filter(ju => ju.error);
            this.store.dispatch(new UpdateJobUpdates(updates));
            this.store.dispatch(new HandleJobUpdateError(errors));
          }
        }),
        catchError(error => {
          // Previously swallowed silently, which hid mapping throws (e.g. a bad
          // timestamp) that quietly killed this channel — surface it.
          console.error('[JobUpdates] stream error — channel closed', error);
          return of(null);
        })
      )
      .subscribe(
        () => console.log('Job updates subscription active'),
        error => console.log('Error in job updates subscription:', error),
        () => console.log('Job updates subscription completed')
      );
  }

  @Action(StopListeningForJobUpdates)
  stopListeningForJobUpdates(ctx: StateContext<JobUpdateStateModel>) {
    this.cleanupExistingSubscription();
    // Forget the job too. ListenForJobUpdates short-circuits when the incoming
    // jobRefId equals the stored one, so leaving it set here makes the next
    // listen for the same job a no-op against a subscription we just tore down
    // — the channel stays dead until a full page reload.
    ctx.patchState({ jobRefId: null });
  }

  // Helper method to clean up existing subscription
  private cleanupExistingSubscription(): void {
    if (this.jobUpdatesUnSubscription) {
      this.jobUpdatesUnSubscription.next();
      this.jobUpdatesUnSubscription.complete();
    }

    if (this.activeSubscription && !this.activeSubscription.closed) {
      this.activeSubscription.unsubscribe();
    }
  }

  @Action(HandleJobUpdateError)
  handleJobUpdateError(ctx: StateContext<JobUpdateStateModel>, { jobUpdateErrors }: HandleJobUpdateError) {
    jobUpdateErrors.forEach(ju => {
      this.notificationService.showError(ju.message);
    });
  }
}
